import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { datadogLogs } from '@datadog/browser-logs'
import { useFlag } from '@openfeature/react-sdk'
import { useNavigate } from 'react-router-dom'

import { FEATURE_FLAG_KEYS } from '@/flags'
import { useReselector } from '@/utils/sharedHooks'
import { getIsRequiredStepsOnboarding } from '@/selectors/user.selectors'
import { useAnalyticsTrack } from '@/features/amplitude'

export const FEATURE_ANNOUNCEMENT_S3_PREFIX =
  'https://heard-images.s3.amazonaws.com/assets/'
export const FEATURE_ANNOUNCEMENT_DISMISSED_KEY =
  'feature_announcement_dismissed'

export interface FeatureAnnouncement {
  announcementId: string
  title: string
  body: string
  ctaLabel: string
  ctaUrl: string
  imageName: string
  eyebrow?: string
  subhead?: string
  disclaimer?: string
  dismissLabel?: string
  startTimestamp?: string
  endTimestamp?: string
}

export interface ResolvedAnnouncement {
  announcement: FeatureAnnouncement
  imageSrc: string
}

export const isSafeCtaUrl = (url: string): boolean =>
  Boolean(url) && url.startsWith('/') && !url.startsWith('//')

export const buildImageSrc = (imageName?: string): string | undefined => {
  if (!imageName) return undefined
  if (/[/\\]|\.\.|:/.test(imageName)) return undefined
  return `${FEATURE_ANNOUNCEMENT_S3_PREFIX}${imageName}`
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

const optionalString = (value: unknown): string | undefined =>
  isNonEmptyString(value) ? value : undefined

type TimestampParse =
  | { ok: true; value: DateTime | undefined }
  | { ok: false; failure: ParseFailure }

const parseOptionalIsoTimestamp = (
  raw: unknown,
  field: 'startTimestamp' | 'endTimestamp'
): TimestampParse => {
  if (!isNonEmptyString(raw)) return { ok: true, value: undefined }
  const dt = DateTime.fromISO(raw)
  if (!dt.isValid) {
    return {
      ok: false,
      failure: { reason: `${field} must be a valid ISO 8601 string` },
    }
  }
  return { ok: true, value: dt }
}

export interface ParseFailure {
  reason: string
}

export type ParseAnnouncementResult =
  | { ok: true; resolved: ResolvedAnnouncement }
  | { ok: false; failure: ParseFailure | null }

const requireField = (
  record: Record<string, unknown>,
  field: keyof FeatureAnnouncement
): { ok: true; value: string } | { ok: false; failure: ParseFailure } => {
  const value = record[field]
  return isNonEmptyString(value)
    ? { ok: true, value }
    : {
        ok: false,
        failure: { reason: `missing or non-string field: ${field}` },
      }
}

/**
 * Pure validation of the LD flag value. Returns `{ ok: true, resolved }` for a
 * valid announcement, `{ ok: false, failure: { reason } }` for a misconfigured
 * flag, and `{ ok: false, failure: null }` for the empty/default flag (the
 * normal "no announcement" state — not an error).
 */
export const parseAnnouncementFlag = (
  raw: unknown,
  now: Date
): ParseAnnouncementResult => {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, failure: null }
  }
  const record = raw as Record<string, unknown>
  if (Object.keys(record).length === 0) {
    return { ok: false, failure: null }
  }

  const announcementId = requireField(record, 'announcementId')
  if (!announcementId.ok) return announcementId
  const title = requireField(record, 'title')
  if (!title.ok) return title
  const body = requireField(record, 'body')
  if (!body.ok) return body
  const ctaLabel = requireField(record, 'ctaLabel')
  if (!ctaLabel.ok) return ctaLabel
  const ctaUrl = requireField(record, 'ctaUrl')
  if (!ctaUrl.ok) return ctaUrl
  const imageName = requireField(record, 'imageName')
  if (!imageName.ok) return imageName

  if (!isSafeCtaUrl(ctaUrl.value)) {
    return {
      ok: false,
      failure: { reason: 'ctaUrl must be an app-relative path (e.g. /foo)' },
    }
  }
  const imageSrc = buildImageSrc(imageName.value)
  if (!imageSrc) {
    return {
      ok: false,
      failure: {
        reason: 'imageName must be a bare S3 filename (no /, .., or protocol)',
      },
    }
  }

  const startParse = parseOptionalIsoTimestamp(
    record.startTimestamp,
    'startTimestamp'
  )
  if (!startParse.ok) return startParse
  const endParse = parseOptionalIsoTimestamp(
    record.endTimestamp,
    'endTimestamp'
  )
  if (!endParse.ok) return endParse

  const nowDt = DateTime.fromJSDate(now)
  if (startParse.value && nowDt < startParse.value) {
    return { ok: false, failure: null }
  }
  if (endParse.value && nowDt > endParse.value) {
    return { ok: false, failure: null }
  }

  const announcement: FeatureAnnouncement = {
    announcementId: announcementId.value,
    title: title.value,
    body: body.value,
    ctaLabel: ctaLabel.value,
    ctaUrl: ctaUrl.value,
    imageName: imageName.value,
    eyebrow: optionalString(record.eyebrow),
    subhead: optionalString(record.subhead),
    disclaimer: optionalString(record.disclaimer),
    dismissLabel: optionalString(record.dismissLabel),
    startTimestamp: optionalString(record.startTimestamp),
    endTimestamp: optionalString(record.endTimestamp),
  }
  return { ok: true, resolved: { announcement, imageSrc } }
}

export const getDismissedAnnouncementIds = (): string[] => {
  try {
    const raw = window.localStorage.getItem(FEATURE_ANNOUNCEMENT_DISMISSED_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : []
  } catch {
    return []
  }
}

export const addDismissedAnnouncementId = (id: string): void => {
  try {
    const ids = getDismissedAnnouncementIds()
    if (ids.includes(id)) return
    window.localStorage.setItem(
      FEATURE_ANNOUNCEMENT_DISMISSED_KEY,
      JSON.stringify([...ids, id])
    )
  } catch {
    // storage unavailable (private mode / disabled) — dismissal is best-effort
  }
}

const SHOWN_EVENT = 'Product announcement shown'
const CLICKED_EVENT = 'Product announcement clicked'
const DISMISSED_EVENT = 'Product announcement dismissed'

export interface UseFeatureAnnouncementResult extends ResolvedAnnouncement {
  onCtaClick: () => void
  onDismiss: () => void
}

export const useFeatureAnnouncement =
  (): UseFeatureAnnouncementResult | null => {
    const { value } = useFlag(FEATURE_FLAG_KEYS.featureAnnouncement, {})
    const isOnboarding = useReselector(getIsRequiredStepsOnboarding)
    const track = useAnalyticsTrack()
    const navigate = useNavigate()

    const parsed = useMemo(
      () => parseAnnouncementFlag(value, new Date()),
      [value]
    )

    useEffect(() => {
      if (!parsed.ok && parsed.failure) {
        datadogLogs.logger.warn(
          'feature-announcement flag failed validation; banner suppressed',
          { reason: parsed.failure.reason, value }
        )
      }
    }, [parsed, value])

    const resolved = parsed.ok ? parsed.resolved : null
    const announcementId = resolved?.announcement.announcementId

    // Persisted dismissals are read synchronously during render so a returning
    // dismissed user has `visible === false` on the FIRST render — preventing
    // a spurious SHOWN event from firing before a useEffect could correct it.
    const persistentlyDismissed = useMemo(
      () =>
        Boolean(
          announcementId &&
          getDismissedAnnouncementIds().includes(announcementId)
        ),
      [announcementId]
    )
    // In-session override flipped by the dismiss / CTA-click handlers below,
    // which also persist via addDismissedAnnouncementId. Reset when a new
    // announcement is delivered (different announcementId).
    const [sessionDismissed, setSessionDismissed] = useState(false)
    useEffect(() => {
      setSessionDismissed(false)
    }, [announcementId])

    const shownRef = useRef<string | null>(null)
    const visible =
      Boolean(resolved) &&
      !isOnboarding &&
      !persistentlyDismissed &&
      !sessionDismissed

    useEffect(() => {
      if (visible && announcementId && shownRef.current !== announcementId) {
        track(SHOWN_EVENT, { announcementId })
        shownRef.current = announcementId
      }
    }, [visible, announcementId, track])

    const onDismiss = useCallback(() => {
      if (!announcementId) return
      addDismissedAnnouncementId(announcementId)
      track(DISMISSED_EVENT, { announcementId })
      setSessionDismissed(true)
    }, [announcementId, track])

    const onCtaClick = useCallback(() => {
      if (!resolved) return
      track(CLICKED_EVENT, {
        announcementId: resolved.announcement.announcementId,
      })
      addDismissedAnnouncementId(resolved.announcement.announcementId)
      setSessionDismissed(true)
      navigate(resolved.announcement.ctaUrl)
    }, [resolved, track, navigate])

    if (!visible || !resolved) return null
    return { ...resolved, onCtaClick, onDismiss }
  }
